// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartPropsGridSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import { MESSAGE } from './Izenda.PropertiesBuilderConstants';

export default {
  schemaKey: 'RP_GRID_TYPE',
  fields: {
    /* general info */
    generalInfo: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('COMMON_LABEL_GENERAL_INFO');
          },
          icon: 'icon-toggle-triangle'
        }
      },
      fields: {
        gridStyle: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_GRID_STYLE');
          },
          component: 'CustomDropdown',
          fnValue: 'getGeneralInfoByKey',
          props: {
            options: 'getGridStyle'
          }
        },
        separatorStyle: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_SEPARATOR_STYLE');
          },
          component: 'CustomDropdown',
          fnValue: 'getGeneralInfoByKey',
          props: {
            options: 'getSeparatorStyle'
          }
        }
      }
    },
    table: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('MODEL_LABEL_TABLE');
          },
          icon: 'icon-toggle-triangle'
        },
        hiddenByState: true
      },
      fields: {
        border: {
          get title() {
            return localizer.getText('COMMON_LABEL_BORDER');
          },
          type: 'btnPopup',
          fnValue: 'getBorderSettings',
          props: {
            hadSetting: 'hadSettingBorder',
            fnResetDefaultValue: 'fnResetBorderDefaultValue',
            loadValue: 'getBorderSettings',
            previewComponent: require('share/components/controls/Izenda.GridBorderPreview.jsx')
          },
          staticProps: {
            get title() {
              return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_GRID_BORDER_SETTINGS');
            },
            className: 'icon-setting btn-icon',
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_GRID_BORDER_SETTINGS');
            },
            popupContent: require('share/components/controls/Izenda.BorderSettings'),
            popupProps: {
              type: 'none'
            }
          }
        },
        // color
        backgroundColor: {
          get title() {
            return localizer.getText('COLOR_STYLING_BACKGROUND_TOOLTIP');
          },
          component: 'ColorStyling',
          fnValue: 'getTableColorStyling',
          props: {
            type: 'photoshop',
            position: 'left',
            options: {
              text: undefined,
              background: true
            }
          }
        },
        alternativeBackground: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_ALTERNATIVE_BACKGROUND');
          },
          type: 'select',
          fnValue: 'getTableAlternativeBackgroundValue',
          props: {
            options: 'getTableAlternativeBackgroundOptions'
          }
        },
        alternativeBackgroundColor: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_ALTERNATIVE_BACKGROUND_COLOR');
          },
          isHidden: 'isHiddenByAlternativeBackground',
          component: 'ColorStyling',
          fnValue: 'getTableAlternativeBackgroundColor',
          props: {
            type: 'photoshop',
            position: 'left',
            options: {
              text: undefined,
              background: true
            }
          }
        }
      }
    },
    columns: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_COLUMNS');
          },
          icon: 'icon-toggle-triangle'
        }
      },
      fields: {
        width: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_WIDTH');
          },
          component: 'SizeSetting',
          fnValue: 'getColumnWidth',
          props: {
            loadValue: 'getWidthValue',
            propertyKey: 'width.value'
          }
        },

        // Text alignment
        columnAlignment: {
          get title() {
            return localizer.getText('FONT_SETTINGS_LABEL_ALIGNMENT');
          },
          component: 'AlignmentSetting',
          fnValue: 'getColumnAlignment'
        },

        // Vertical alignment
        columnVerticalAlignment: {
          title: '',
          component: 'VerticalAlignmentSetting',
          fnValue: 'getColumnVerticalAlignment'
        },

        // wordwrap
        columnWordWrap: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_WORD_WRAP');
          },
          component: 'ButtonHighlight',
          fnValue: 'getColumnsByKey',
          staticProps: {
            defaultIcon: 'icon-word-wr'
          },
          props: {
            loadValue: 'getColumnsWordWrapValue',
            propertyKey: 'wordWrap',
            get description() {
              return localizer.getText('BUTTON_HIGHLIGHT_WRAP_TEXT');
            },
            states: [
              {
                value: true,
                icon: 'icon-word-wr active'
              },
              {
                value: false,
                icon: 'icon-word-wr'
              }
            ],
            disabledIcon: 'icon-word-wr'
          }
        }
      }
    },
    headers: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_HEADERS');
          },
          icon: 'icon-toggle-triangle'
        }
      },
      fields: {
        //font family + font size
        fontFormat: {
          get title() {
            return localizer.getText('FONT_SETTINGS_LABEL_FONT');
          },
          component: 'FontFormat',
          fnValue: 'getHeaderFontFormat'
        },

        // bold, italic, underline
        fontStyle: {
          title: '',
          component: 'FontStyle',
          fnValue: 'getHeaderFontStyle'
        },

        // color
        color: {
          get title() {
            return localizer.getText('COMMON_LABEL_COLOR');
          },
          component: 'ColorStyling',
          fnValue: 'getHeaderColorStyling',
          props: {
            type: 'photoshop',
            position: 'left'
          }
        },

        // alignment: left, right, center,...
        alignment: {
          get title() {
            return localizer.getText('FONT_SETTINGS_LABEL_ALIGNMENT');
          },
          component: 'AlignmentSetting',
          fnValue: 'getHeadersByKey'
        },

        // Vertical align: top, middle, bottom
        verticalAlignment: {
          title: '',
          component: 'VerticalAlignmentSetting',
          fnValue: 'getHeadersByKey'
        },

        // wordwrap
        wordWrap: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_WORD_WRAP');
          },
          component: 'ButtonHighlight',
          fnValue: 'getHeadersByKey',
          staticProps: {
            defaultIcon: 'icon-word-wr'
          },
          props: {
            loadValue: 'getWordWrapValue',
            propertyKey: 'wordWrap',
            get description() {
              return localizer.getText('BUTTON_HIGHLIGHT_WRAP_TEXT');
            },
            states: [
              {
                value: true,
                icon: 'icon-word-wr active'
              },
              {
                value: false,
                icon: 'icon-word-wr'
              }
            ],
            disabledIcon: 'icon-word-wr'
          }
        },

        freezeHeaders: {
          get title() {
            return localizer.getText('REPORT_PART_GRID_CONSTANTS_LABEL_FREEZE_HEADERS');
          },
          type: 'select',
          fnValue: 'getFreezeHeaderValue',
          props: {
            options: 'getFreezeHeaderOptions',
            filterOptions: 'filterFreezeHeaderOptions',
            blankOption: true
          }
        },

        disableRepeatHeaders: {
          get title() {
            return localizer.getText('REPORT_PART_GRID_CONSTANTS_LABEL_DISABLE_REPEAT_HEADERS');
          },
          type: 'checkbox',
          fnValue: 'getHeadersByKey',
          isHidden: 'isNotVerticalGrid',
          props: {
            hiddenByState: true
          }
        },

        removeHeaderForExport: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_REMOVE_HEADER_FOR_EXPORT');
          },
          fnValue: 'getHeadersByKey',
          type: 'checkbox',
          props: {
            hiddenByState: true
          }
        },
        removeHeaderForViewer: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_REMOVE_HEADER_FOR_VIEWER');
          },
          isHidden: 'isGridDrillDownOrPivot',
          fnValue: 'getHeadersByKey',
          type: 'checkbox',
          props: {
            hiddenByState: true
          }
        }
      }
    },
    grouping: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('COMMON_LABEL_GROUPING');
          },
          icon: 'icon-toggle-triangle'
        },
        hiddenByState: true
      },
      fields: {
        useSeparator: {
          get title() {
            return localizer.getText('COMMON_LABEL_USE_SEPARATOR');
          },
          fnValue: 'getGroupingByKey',
          type: 'checkbox',
          props: {
            beforeOnChange: 'beforeChangeSeparator'
          }
        }
      }
    },
    view: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('MODEL_LABEL_VIEW');
          },
          icon: 'icon-toggle-triangle'
        }
      },
      fields: {
        dataRefreshInterval: {
          get title() {
            return localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING');
          },
          type: 'btnPopup',
          fnValue: 'getDataRefreshInterval',
          props: {
            hadSetting: 'hadSettingRefreshInterval',
            fnResetDefaultValue: 'fnResetRefreshIntervalDefaultValue',
            hiddenByState: true,
            loadValue: 'getDataRefreshInterval'
          },
          staticProps: {
            get title() {
              return localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING');
            },
            className: 'icon-setting btn-icon',
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_DATA_REFRESH_INTERVAL');
            },
            popupContent: require('modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.DataRefreshSetting'),
            popupProps: {
              type: 'none',
              autoClose: false
            }
          }
        },
        conditionalFormatting: {
          get title() {
            return localizer.getText('COMMON_LABEL_CONDITIONAL_FORMATTING_SETTING');
          },
          type: 'btnPopup',
          fnValue: 'getConditionalFormatting',
          props: {
            hadSetting: 'hadConditionalFormattingSetting',
            fnResetDefaultValue: 'fnResetConditionalFormattingDefaultValue',
            hiddenByState: true,
            loadValue: 'getConditionalFormattingWithReportPart'
          },
          staticProps: {
            get title() {
              return localizer.getText('COMMON_LABEL_CONDITIONAL_FORMATTING_SETTING');
            },
            className: 'icon-setting btn-icon',
            get popupTitle() {
              return localizer.getText('COMMON_LABEL_CONDITIONAL_FORMATTING_SETTING');
            },
            popupContent:require('modules/ReportDesigner/Fields/PropertiesBuilder/ConditionalFormatting/Izenda.ConditionalFormattingSetting'),
            popupProps: {
              type: 'none',
              autoClose: false,
              sizeClass: 'modal-lg'
            }
          }
        },
        usePagination: {
          get title() {
            return localizer.getText('COMMON_LABEL_USE_PAGINATION');
          },
          fnValue: 'getViewByKey',
          type: 'checkbox'
        },
        expanderRowsSpacing: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_EXPANDER_ROW_SPACING')
          },
          fnValue: 'getViewByKey',
          type: 'checkbox',
          isHidden: 'isExpanderRowsSpacingHidden'
        },
        collapseDrilldownByDefault: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_COLLAPSE_DRILLDOWN_BY_DEFAULT');
          },
          fnValue: 'getViewByKey',
          type: 'checkbox',
          isHidden: 'isNotGridDrillDown'
        },
        pivotColumnsPerExportedPage: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_EXPORT_COLUMNS_PER_PAGE');
          },
          fnValue: 'getViewByKey',
          type: 'number',
          fnProps: 'getPivotColumnsPerExportedPageProps',
          staticProps: {
            isDecimal: false
          }
        }
      }
    },
    printing: {
      component: 'Panel',
      props: {
        initState: true,
        collapsible: true,
        header: {
          get title() {
            return localizer.getText('COMMON_LABEL_PRINTINGS');
          },
          icon: 'icon-toggle-triangle'
        },
        hiddenByState: true
      },
      fields: {
        usePageBreakAfterSeparator: {
          get title() {
            return localizer.getText('REPORT_PART_PROPS_GRID_SCHEMA_LABEL_PAGE_BREAK_AFTER_SEPARATOR');
          },
          fnValue: 'getPageBreakAfterSeparator',
          type: 'checkbox'
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportPartPropsGridSchema.js